<script setup>
import { computed, onMounted, ref, watch } from "vue";
import TagApi from "@api/tag.api";
import FormFileUpload from "@/components/form/FormFileUpload.vue";
import { useMetadataStore } from "@store/metadata";
import GroupFormElSelect from "@/components/form/GroupFormElSelect.vue";
import HandleGroupSelectData from "@/services/HandleGroupSelectData";

const state = useMetadataStore();
const metadata = computed(() => state.getMetaData);

// Get tags from metadata store
const tagsFromMeta = computed(() => metadata.value.tags || []);

// Convert tags to the same format as usersForSelect
const tagsForSelect = computed(() => {
    return tagsFromMeta.value
        .filter((tag) => tag && tag.name && typeof tag.name === "string")
        .map((tag) => ({
            id: tag.id,
            name: tag.name,
        }));
});

const documentTypesForSelect = computed(() => {
    const raw = metadata.value.documentTypes ?? [];
    return HandleGroupSelectData(raw) ?? [];
});
const usersForSelect = computed(() => metadata.value.users ?? []);

const props = defineProps({
    document: {
        type: Object,
    },
    v$: {
        type: Object,
    },
    formErrors: {
        type: Object,
    },
    isUpdateForm: {
        type: Boolean,
    },
});

// Inline required validation for files (create mode only)
const filesErrors = computed(() => {
    const errs = Array.isArray(props.formErrors?.files)
        ? [...props.formErrors.files]
        : [];
    const isCreate = !props.document?.id;
    const noFiles =
        !Array.isArray(props.document?.files) ||
        props.document.files.length === 0;
    if (isCreate && noFiles) {
        errs.push("Please upload at least one file.");
    }
    return errs;
});

const onUploadSuccess = (response) => {
    if (!props.document.files) props.document.files = [];

    // Store full file object (not only ID)
    props.document.files = [{
        id: response.file.id,
        name: response.file.name,
        size: response.file.size,
        type: response.file.type,
        uid: response.file.uid || response.file.id,
    }];
};

const onUploadDelete = (file) => {
    // Ensure files array exists
    // if (!props.document.files) {
    //     props.document.files = [];
    //     return;
    // }

    delete props.document.files ;
    // const fileIndex = props.document.files.findIndex(
    //     (fileId) => fileId === file.id
    // );
    // if (fileIndex !== -1) {
    //     props.document.files.splice(fileIndex, 1);
      
    // }
};

// Simple tag creation function for form-el-select
const createTag = async (tagName) => {
    try {
        // Create the tag in the database (firstOrCreate handles duplicates)
        const response = await TagApi.store({ name: tagName });

        // Add the new tag to the metadata store
        const newTag = {
            id: response.data.data.tag.id,
            name: response.data.data.tag.name,
        };

        // Ensure the newly created tag is selected by default
        if (!Array.isArray(props.document.tags)) {
            props.document.tags = [];
        }

        // If a temporary label was added by the select, replace it with the new ID
        const lastIdx = props.document.tags.lastIndexOf(tagName);
        if (lastIdx !== -1) {
            props.document.tags.splice(lastIdx, 1, newTag.id);
        } else if (!props.document.tags.includes(newTag.id)) {
            props.document.tags.push(newTag.id);
        }

        // Update metadata store with new tag
        if (typeof state.addTag === "function") {
            state.addTag(newTag);
        } else {
            // Fallback: manually add to metadata
            if (!state.metadata.tags) {
                state.metadata.tags = [];
            }
            const existingTag = state.metadata.tags.find(
                (t) => t.id === newTag.id
            );
            if (!existingTag) {
                state.metadata.tags.push(newTag);
            }
        }

        return newTag;
    } catch (error) {
        console.error("Error creating tag:", error);
        throw error;
    }
};

onMounted(async () => {
    // Ensure metadata is loaded first
    if (!state.loaded) {
        await state.fetchData();
    }

    // Initialize tags array if it doesn't exist
    if (!props.isUpdateForm) {
        props.document.tags = [];
    }
});
</script>

<template>
    <div>
        <template v-if="!document.id">
            <div class="row m-0 my-2">
                <div class="col-md-12 p-0">
                    <label class="form-label mb-2">
                        Upload Document
                        <span class="text-danger astric"> *</span></label
                    >
                    <FormFileUpload
                        :allowed-files="'.jpg, .jpeg, .png, .pdf, .doc, .docx, .xls, .xlsx, .csv, .ppt, .pptx, .mp4, .mov, .vsdx'"
                        :errors="filesErrors"
                        :limit="10"
                        :model="document"
                        name="files"
                        :multiple="false"
                        :required="true"
                        clearable
                        label="Upload Files"
                        type="document_attachment"
                        @successUpload="onUploadSuccess"
                        @successDeleteUpload="onUploadDelete"

                    >
                    </FormFileUpload>

                    <small
                        v-for="(error, index) in filesErrors || []"
                        :key="index"
                        class="text-danger"
                        style="font-size: 0.8rem; top: 40px"
                    >
                        {{ error }}
                    </small>
                </div>
            </div>
        </template>
        <div class="row my-2">
            <div class="col-md-6">
                <form-input
                    id="name"
                      v-model="document.title"
                    :errors="v$?.title"
                    :label="$t('fields.document_title')"
                    :required="true"
                    placeholder="What is this document about?"
                    :maxlength="120"
                    type="text"
                />
            </div>
            <div class="col-md-6">
                <group-form-el-select
                    :errors="formErrors"
                    :label="$t('fields.document_type_input')"
                    :model="document"
                    :options="documentTypesForSelect"
                    :required="true"
                    clearable
                    label-field="name"
                    group-by-field="group"
                    name="document_type_id"
                    placeholder="Select whether this is a Case Study, EM Guide, CXO etc"
                />
                <small
                    v-for="error in v$?.document_type_id?.$errors || []"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
        </div>
        <div class="row my-2">
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                  v-model="document.document_context"
                    :errors="v$?.document_context"
                    :label="$t('fields.document_context')"
                    :required="true"
                    placeholder="When/why this was created?"
                    rows="4"
                />
            </div>
            <div class="col-md-12 mb-3">
                <form-tiptap-text-area
                    id="name"
                    v-model="document.document_purpose"
                    :errors="v$?.document_purpose"
                    :label="$t('fields.document_purpose')"
                    :required="true"
                    placeholder="How will it help TAMers in future?"
                    rows="4"
                />
            </div>

            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="$t('fields.authors')"
                    :model="document"
                    :multiple="true"
                    :options="usersForSelect"
                    :required="true"
                    clearable
                    label-field="fields.team_ids"
                    name="authors_ids"
                    placeholder="Select the TAMers who worked on creating this document."
                />

                <small
                    v-for="error in v$?.authors_ids?.$errors || []"
                    :key="error.$uid"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error.$message }}
                </small>
            </div>
            <div class="col-md-6">
                <form-el-select
                    :errors="formErrors"
                    :label="'Tags'"
                    :model="document"
                    :multiple="true"
                    :options="tagsForSelect"
                    :required="false"
                    :allow-create="true"
                    :on-create-tag="createTag"
                    clearable
                    label-field="name"
                    name="tags"
                    placeholder="AI generated Smart Tags"
                />
                <small
                    v-for="(error, index) in formErrors?.tags || []"
                    :key="index"
                    class="text-danger"
                    style="font-size: 0.8rem; top: 40px"
                >
                    {{ error }}
                </small>
            </div>
        </div>
    </div>
</template>
